import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BuyInAmountBox from './BuyInAmountBox';
import { autoTopUpRest, joinTableRest, rebuyTableRest } from '@/services/table/tableService';
import { TABLE_CONSTANTS } from '@/constants/TABLE';
import { LobbyTableCloseBuyInAction } from '@/services/lobby/lobbyActions';
import Checkbox from '../../../components/new-design/Checkbox';
import useIsMobileView from '../../../hooks/useIsMobileView';
import { store } from '@/services/app/store';
import {
  useAccountAbstractionDispatch,
  useAccountAbstractionStore,
} from '@/features/account-abstraction/store/AccountAbstractionContext';
import { joinTableContract } from '@/pages/table/web3/joinTable';
import { rebuyTableContract } from '@/pages/table/web3/rebuyTable';
import { ReducerState } from '@/services/common/lib/reducerState';
import {
  setLoadingTransaction,
  deleteJoinTableSession,
  deleteRebuySession,
} from '@/features/account-abstraction/store/accountAbstractionActions';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import { useWalletAddress } from '@/features/account-abstraction/hooks/useWalletAddress';
import config from '@/common/config';
import { formatUnits, parseUnits } from 'viem';
import useWalletBalance from '@/hooks/useWalletBalance';
import { useToast } from '@/components/toast/useToast';
import { isSessionError } from '@/features/account-abstraction/lib/isSessionError';
import Button, { ButtonVariants } from '@/components/new-design/Button';
import { twMerge } from 'tailwind-merge';
import Modal from '@/components/Modal/Modal';
import useTimer, { TimeUnit } from '@/hooks/useTimer';
import useRetryRequest from './useRetryRequest';
import { useDebouncedCallback } from 'use-debounce';
import { IoChevronDown } from 'react-icons/io5';
import AutoTopUpInput from './AutoTopUpInput';
import { tableJoining } from '@/services/table/tableActions';
import { ReactComponent as WarningTriangle } from '@/assets/warning-triangle.svg';
import { useSeatReservation } from '../hooks/useSeatReservation';
import {
  selectIsRealMoney,
  selectMaxBuyin,
  selectMinBuyin,
  selectSeats,
  selectTableUrl,
} from '@/services/table/tableSelectors';
import * as Sentry from '@sentry/react';
import { selectUserId } from '@/services/auth/authSelectors';

interface BuyInParameters {
  tableId: string;
  seat: number;
  stack: number;
  txId?: `0x${string}`;
  seatReserved: boolean;
  onClose: () => void;
  isOpen: boolean;
  isRebuy?: boolean;
  showTimer?: boolean;
}

const BuyIn: React.FC<BuyInParameters> = ({
  tableId,
  seat: seatFromParams,
  stack: stackFromParams,
  txId,
  seatReserved: seatReservedFromParams,
  onClose,
  isOpen,
  isRebuy = false,
  showTimer = true,
}) => {
  const dispatch = useDispatch();

  const { seatReserved, stack, seat } = useSeatReservation(seatReservedFromParams, seatFromParams, stackFromParams);

  const { toast } = useToast();
  const isMobileView = useIsMobileView();
  const contractId = useSelector((state: ReducerState) => state.tables?.[tableId]?.contractId);
  const accountAbstractionStore = useAccountAbstractionStore();
  const accountAbstrationDispatch = useAccountAbstractionDispatch();
  const { initialTime, time, isDone } = useTimer(1, TimeUnit.Minutes);
  const [shouldRetryRequest, setShouldRetryRequest] = useState(false);
  const [shouldRetryRebuyRequest, setShouldRetryRebuyRequest] = useState(false);
  const [joiningTable, setJoiningTable] = useState(false);
  const [autoTopUpSettingsOpen, setAutoTopUpSettingsOpen] = useState(false);

  const { walletAddress, isLoading: isLoadingWallet } = useWalletAddress();
  const { data, isLoading: isLoadingBalance } = useWalletBalance(walletAddress, config.contracts.usdt);
  const tableUrl = useSelector(selectTableUrl(tableId));
  const maxBuyin = useSelector(selectMaxBuyin(tableId));
  const minBuyin = useSelector(selectMinBuyin(tableId));
  const isRealMoney = useSelector(selectIsRealMoney(tableId));
  const tableSeats = useSelector(selectSeats(tableId));
  const userId = useSelector(selectUserId);

  const seatIsReserved = useMemo(() => {
    return !!tableSeats?.find((_seat) => _seat?.seat === seat && _seat?.userId === userId);
  }, [tableSeats, seat, userId]);

  const balance = useMemo(() => {
    if (!isRealMoney) return 1000000;
    if (!data?.value || !data?.decimals) return 0;
    return parseFloat(formatUnits(data.value, data.decimals));
  }, [isRealMoney, data?.value, data?.decimals]);

  const playerSeat = useSelector((state: ReducerState) => state.tables?.[tableId]?.playerSeat);

  const [autoTopUp, setAutoTopUp] = useState<boolean>(playerSeat?.autoTopup || false);
  const [autoTopUpEdited, setAutoTopUpEdited] = useState(false);
  const currentStack = useMemo(() => playerSeat?.stack || 0, [playerSeat?.stack]);
  const maxBuyIn = useMemo(() => {
    if (!maxBuyin) return balance;
    const maxBuyinOrStack = Math.max(maxBuyin, stack);
    return Number(maxBuyinOrStack - currentStack);
  }, [stack, currentStack, maxBuyin, balance]);

  useEffect(() => {
    if (isDone) {
      handleClose();
    }
  }, [isDone]);

  const minBuyIn = useMemo(() => {
    if (!minBuyin) return 0;
    const minBuyinOrStack = Math.max(minBuyin, stack);
    return Number(Math.max(minBuyinOrStack - currentStack, TABLE_CONSTANTS.minBuyIn));
  }, [stack, currentStack, minBuyin]);

  const isInHand = useMemo(() => {
    return playerSeat?.status === true && playerSeat?.active === true;
  }, [playerSeat]);

  const defaultBuyInAmount = useMemo(() => {
    if (isInHand || maxBuyIn < 0) {
      return 0;
    }

    return maxBuyIn || 0;
  }, [isInHand, maxBuyIn]);

  const [buyInAmount, setBuyInAmount] = useState<number>(defaultBuyInAmount);
  const [autoTopUpBuyIn, setAutoTopUpBuyIn] = useState<number | undefined>(playerSeat?.autoTopUpBuyIn || undefined);
  const [autoTopUpThreshold, setAutoTopUpThreshold] = useState<number | undefined>(playerSeat?.autoTopUpThreshold || 0);

  const isLoading = useMemo(
    () =>
      (isRealMoney && (isLoadingBalance || isLoadingWallet)) ||
      accountAbstractionStore.state.loading ||
      accountAbstractionStore.state.loadingJoinTableSession ||
      accountAbstractionStore.state.loadingRebuySession ||
      accountAbstractionStore.state.loadingTransaction,
    [
      isRealMoney,
      isLoadingBalance,
      isLoadingWallet,
      accountAbstractionStore.state.loading,
      accountAbstractionStore.state.loadingJoinTableSession,
      accountAbstractionStore.state.loadingRebuySession,
      accountAbstractionStore.state.loadingTransaction,
    ],
  );

  const canBuyIn = useMemo(() => {
    if (!isRealMoney) return seatReserved;

    if (autoTopUp === true && (autoTopUpBuyIn || 0) <= (autoTopUpThreshold || 0)) {
      return false;
    }

    if (!minBuyin || !maxBuyin || !data?.value || !data?.decimals || !seatReserved) {
      return false;
    }

    if (data?.value < parseUnits(`${buyInAmount}`, data?.decimals)) return false;

    if (buyInAmount === 0 && !isRebuy) {
      return minBuyin === 0;
    }

    const _minBuyIn = Math.max(minBuyIn, stack);
    const _maxBuyIn = Math.max(maxBuyIn, stack);

    const lessThanMax = buyInAmount <= _maxBuyIn;
    const greaterThanMin = buyInAmount >= _minBuyIn;
    const lessThanBalance = buyInAmount <= balance;

    // on rebuy, don't disable the modal if the buyin amount is 0
    // instead we will only update the auto top up settings on modal confirm
    if (isRebuy && buyInAmount === 0) {
      return lessThanMax && lessThanBalance;
    }

    return greaterThanMin && lessThanMax && lessThanBalance;
  }, [
    balance,
    buyInAmount,
    data?.decimals,
    data?.value,
    maxBuyIn,
    minBuyIn,
    stack,
    maxBuyin,
    minBuyin,
    isRealMoney,
    seatReserved,
    isRebuy,
    autoTopUpThreshold,
    autoTopUpBuyIn,
    autoTopUp,
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const joinTable = useCallback(
    async (tableId: string, seat: number) => {
      if (!tableId) {
        alert('You are not joining to a valid table');
        return;
      }
      if (!seatIsReserved) {
        toast({ description: 'Seat is not reserved' });
        return;
      }

      try {
        setShouldRetryRequest(false);
        setJoiningTable(true);
        dispatch(tableJoining(tableId, true));
        const playerId: string | null | undefined = store.getState().auth.id;

        if (!playerId) throw new Error('No playerId');

        if (!buyInAmount) {
          return;
        }

        accountAbstrationDispatch(setLoadingTransaction(true));

        let joinTableTxId: string | undefined = txId;

        if (isRealMoney) {
          let retries = 0;
          const maxRetries = 3;
          const retryDelay = 5000; // 5 seconds

          while (retries < maxRetries) {
            if (contractId) {
              break;
            }

            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            retries++;
          }

          if (!contractId) throw new Error('No contractId after retries');

          if (!joinTableTxId) {
            const result = await joinTableContract(
              accountAbstractionStore.state,
              BigInt(contractId),
              String(buyInAmount),
            );

            joinTableTxId = result?.transactionHash;
          }
          if (!joinTableTxId) throw new Error('Invalid transaction');
        }

        await Promise.all([
          joinTableRest({
            tableId: tableId,
            seat: seat,
            buyIn: buyInAmount,
            txId: joinTableTxId,
            tableUrl,
          }),
          autoTopUpRest({
            tableId,
            autoTopUp,
            autoTopUpBuyIn,
            autoTopUpThreshold,
            tableUrl,
          }),
        ]);

        // Remove seat and stack from search params without refreshing
        const removeSearchParams = () => {
          const url = new URL(window.location.href);
          url.searchParams.delete('seat');
          url.searchParams.delete('stack');
          window.history.replaceState({}, '', url);
        };

        removeSearchParams();

        dispatch(LobbyTableCloseBuyInAction(tableId));
      } catch (err: unknown) {
        if (err instanceof Error) {
          if (isSessionError(err.message)) {
            accountAbstrationDispatch(deleteJoinTableSession());
            setShouldRetryRequest(true);
            Sentry.captureException(err);
          } else if ((err as any).response?.status !== 429) {
            toast({ description: `There was an error joining the table.` });
            Sentry.captureException(err);
          }
        } else if ((err as any).response?.status !== 429) {
          toast({ description: 'Unknown error joining table' });
          Sentry.captureException(err);
        }
      } finally {
        accountAbstrationDispatch(setLoadingTransaction(false));
        setJoiningTable(false);
        setIsSubmitting(false);
      }
    },
    [
      buyInAmount,
      accountAbstrationDispatch,
      isRealMoney,
      tableUrl,
      autoTopUp,
      autoTopUpBuyIn,
      autoTopUpThreshold,
      dispatch,
      contractId,
      accountAbstractionStore.state,
      txId,
      toast,
      seatIsReserved,
    ],
  );

  const rebuy = useCallback(
    async (tableId: string) => {
      if (!tableId) {
        alert('You are not joining to a valid table');
        return;
      }
      try {
        setShouldRetryRebuyRequest(false);
        const playerId: string | null | undefined = store.getState().auth.id;

        if (!playerId) throw new Error('No playerId');

        if (!buyInAmount && !isRebuy) {
          return;
        }

        // allow user to change auto top up settings without rebuying
        if (isRebuy && buyInAmount === 0) {
          await autoTopUpRest({
            tableId,
            autoTopUp,
            autoTopUpBuyIn,
            autoTopUpThreshold,
            tableUrl,
          });

          dispatch(LobbyTableCloseBuyInAction(tableId));

          return;
        }

        accountAbstrationDispatch(setLoadingTransaction(true));

        let txId;

        if (isRealMoney) {
          if (!contractId) throw new Error('No contractId');

          const result = await rebuyTableContract(
            accountAbstractionStore.state,
            BigInt(contractId),
            String(buyInAmount),
          );

          txId = result?.transactionHash;
        }

        await Promise.all([
          rebuyTableRest({
            tableId,
            buyIn: buyInAmount,
            txId,
            tableUrl,
          }),
          autoTopUpRest({
            tableId,
            autoTopUp,
            autoTopUpBuyIn,
            autoTopUpThreshold,
            tableUrl,
          }),
        ]);

        dispatch(LobbyTableCloseBuyInAction(tableId));
      } catch (err: unknown) {
        console.log('err rebuy', err);
        if (err instanceof Error) {
          if (isSessionError(err.message)) {
            accountAbstrationDispatch(deleteRebuySession());
            setShouldRetryRebuyRequest(true);
            Sentry.captureException(err);
          } else {
            toast({
              description: `There was an error processing the rebuy request.`,
            });
            Sentry.captureException(err);
          }
        } else {
          toast({ description: 'Unknown error rebuying' });
          Sentry.captureException(err);
        }
      } finally {
        accountAbstrationDispatch(setLoadingTransaction(false));
        setIsSubmitting(false);
      }
    },
    [
      buyInAmount,
      accountAbstrationDispatch,
      isRealMoney,
      tableUrl,
      dispatch,
      contractId,
      accountAbstractionStore.state,
      autoTopUp,
      autoTopUpBuyIn,
      autoTopUpThreshold,
      toast,
      isRebuy,
    ],
  );

  const handleOnClickConfirm = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (isRebuy) {
        await rebuy(tableId);
      } else {
        await joinTable(tableId, seat);
      }
    } catch (error) {
      console.error('Error buying in', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isRebuy, rebuy, joinTable, tableId, seat]);

  const debouncedHandleOnClickConfirm = useDebouncedCallback(handleOnClickConfirm, 500);

  const handleTopUp = (e: React.FormEvent) => {
    const isChecked = (e?.target as HTMLInputElement)?.checked;

    if (isChecked) {
      let defaultAutoTopUpAmount = buyInAmount;

      // lowest top up amount allowed is 0.5
      if (defaultAutoTopUpAmount < 0.5) {
        defaultAutoTopUpAmount = 0.5;
      }

      setAutoTopUpBuyIn(defaultAutoTopUpAmount);
      setAutoTopUpThreshold(0);
    } else {
      setAutoTopUpBuyIn(undefined);
      setAutoTopUpThreshold(undefined);
    }

    setAutoTopUp(isChecked);

    if (!autoTopUpEdited) {
      setAutoTopUpEdited(true);
    }
  };

  const handleClose = () => {
    if (!joiningTable) {
      onClose();
    }
  };

  useRetryRequest(
    joinTable,
    [tableId, seat],
    shouldRetryRequest &&
      accountAbstractionStore?.state?.joinTableBatchSession &&
      !accountAbstractionStore?.state?.loading &&
      !accountAbstractionStore?.state?.loadingTransaction &&
      !isSubmitting &&
      seatIsReserved &&
      // ensure that the request doesn't retry if the seat is no longer reserved
      !(showTimer && isDone),
  );
  useRetryRequest(
    rebuy,
    [tableId],
    shouldRetryRebuyRequest &&
      accountAbstractionStore?.state?.rebuyBatchSession &&
      !accountAbstractionStore?.state?.loading &&
      !accountAbstractionStore?.state?.loadingTransaction &&
      !isSubmitting,
  );
  return (
    <Modal
      open={isOpen}
      setClose={handleClose}
      className="max-w-[500px]"
      titleComponent={<Modal.Title onClose={handleClose} title={isRebuy ? 'Rebuy' : 'Buy In'} />}
    >
      <div className={twMerge('flex h-full w-full flex-col justify-between')}>
        <div>
          {isInHand ? (
            <div className={twMerge('mb-4 text-sm text-red-500')}>
              Please finish your current hand before adding funds
            </div>
          ) : (
            <div className={twMerge('modal-text mb-4')}>Enter your buy in amount for the table</div>
          )}
          {maxBuyIn < 0 && (
            <div className="my-4 flex flex-row items-center gap-2 bg-[#C54528]/[0.1] px-3 py-2">
              <WarningTriangle />
              <p className="text-sm text-[#C54528]">Your stack is over the max buyin</p>
            </div>
          )}

          <div className="mb-4">
            <BuyInAmountBox
              label={'Amount'}
              amount={buyInAmount}
              setAmount={setBuyInAmount}
              balance={balance}
              minValue={minBuyIn}
              maxValue={maxBuyIn}
              seatReserved={seatReserved}
              initialTimer={initialTime}
              time={time}
              showTimer={showTimer}
              isRebuy={isRebuy}
              autoTopUp={autoTopUp}
              isInHand={isInHand}
            />
          </div>
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-1 flex-row justify-between">
              <Checkbox checked={autoTopUp} onChange={handleTopUp} text="Auto top-up" />
              {(shouldRetryRequest || shouldRetryRebuyRequest) &&
                !accountAbstractionStore.state?.loadingTransaction && (
                  <p className="animate-pulse">Refreshing Session...</p>
                )}
            </div>
            {autoTopUp && (
              <button
                className="modal-text flex flex-row items-center justify-between gap-1 !text-sm"
                onClick={() => setAutoTopUpSettingsOpen(!autoTopUpSettingsOpen)}
              >
                Customize
                <IoChevronDown
                  size={20}
                  className={twMerge('text-ph-gray-evening', autoTopUpSettingsOpen && 'rotate-180 transform')}
                />
              </button>
            )}
          </div>
          {autoTopUpSettingsOpen && autoTopUp && (
            <div className="flex w-full flex-row items-center justify-between py-2">
              <div className="modal-buy-in-amount flex w-full flex-row gap-4 rounded-[1.5em] px-6 py-4">
                <AutoTopUpInput
                  value={autoTopUpBuyIn}
                  label="Top Up To:"
                  onChange={(val) => {
                    if (!autoTopUpEdited) {
                      setAutoTopUpEdited(true);
                    }
                    setAutoTopUpBuyIn(val);
                  }}
                />
                <AutoTopUpInput
                  value={autoTopUpThreshold}
                  label="Top Up When Below:"
                  onChange={(val) => {
                    if (!autoTopUpEdited) {
                      setAutoTopUpEdited(true);
                    }
                    setAutoTopUpThreshold(val);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className={`flex w-full flex-row-reverse gap-2 pt-6 ${isMobileView ? 'w-full' : 'mr-auto'}`}>
          <Button
            disabled={
              !canBuyIn ||
              !seatReserved ||
              isLoading ||
              (showTimer === true && isDone === true) ||
              shouldRetryRequest ||
              isSubmitting ||
              (isInHand && autoTopUpEdited === false)
            }
            onClick={debouncedHandleOnClickConfirm}
            variant={ButtonVariants.primary}
            className={twMerge(
              isLoading ? 'border-2 border-ph-gray-dark bg-transparent text-ph-gray-dark' : '',
              isMobileView ? 'flex grow' : '',
            )}
          >
            {isLoading || shouldRetryRequest || shouldRetryRebuyRequest || isSubmitting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="5" />{' '}
                {accountAbstractionStore.state?.loadingTransaction || isSubmitting ? 'Processing' : 'Loading'}
              </span>
            ) : (
              'Confirm'
            )}
          </Button>
          <Button
            disabled={isLoading || joiningTable || isSubmitting}
            onClick={handleClose}
            variant={ButtonVariants.secondary}
            className={twMerge(isMobileView ? 'flex grow' : '')}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BuyIn;
